import * as Cesium from 'cesium'
import aircraft from '/@/assets/images/aircraft.gltf'
/**
 * 相机视锥体可视化类
 * 用于在Cesium场景中显示相机视锥体、轮廓和模型
 */
export class CameraFrustumVisualizer {
  private position: any
  private heading: number
  private pitch: number
  private roll: number
  private fov: number
  private near: number
  private far: number
  private aspectRatio: any
  private color: number[]
  private viewer: any
  private modelUrl: string
  private modelScale: number
  private frustumPrimitive: null
  private frustumGeometry: null
  private outlinePrimitive: null
  private model: null
  private COLOR_FORMAT: Cesium.VertexFormat
  private DEFAULT_OUTLINE_COLOR: Cesium.Color
  constructor (options: { position: any; heading: number; pitch: number; roll: number; fov: number; near: number; far: number; aspectRatio: any; color: number[]; viewer: any; modelUrl: string; modelScale: number }) {
    // 相机参数
    this.position = options.position
    this.heading = options.heading || 0
    this.pitch = options.pitch || 90
    this.roll = options.roll || 0
    this.fov = options.fov || 30
    this.near = options.near || 10
    this.far = options.far || 200
    this.aspectRatio = options.aspectRatio

    // 可视化参数
    this.color = options.color || [0, 0, 0]
    this.viewer = options.viewer
    this.modelUrl = aircraft
    this.modelScale = options.modelScale || 0.1

    // Cesium对象引用
    this.frustumPrimitive = null
    this.frustumGeometry = null
    this.outlinePrimitive = null
    this.model = null

    // 常量
    this.COLOR_FORMAT = Cesium.VertexFormat.POSITION_ONLY
    this.DEFAULT_OUTLINE_COLOR = new Cesium.Color(0, 201 / 255, 136 / 255, 0.6)
    this.addGlbModel()
  }

  /**
     * 添加所有可视化元素
     */
  add () {
    this.clear()

    if (!this.position) {
      console.warn('Cannot add frustum visualizer: position is undefined')
      return
    }

    this._updateOrientation()
    this._addFrustum()
    this._addOutline()
  }

  /**
     * 更新相机位置和姿态
     * @param {Cesium.Cartesian3} position - 新位置
     * @param {number} heading - 新航向角
     * @param {number} pitch - 新俯仰角
     */
  update (position: any, heading: number, pitch: any) {
    this.position = position
    this.pitch = pitch
    this.heading = heading + 90 // 调整为Cesium坐标系

    this.add()
    this._updateModelPosition()
  }

  /**
     * 更新视锥体参数
     * @param {Cesium.Cartesian3} position - 新位置
     * @param {Object} orientation - 新姿态 {heading, pitch, roll}
     */
  updateFrustum (position: any, orientation: { pitch: number | undefined; heading: undefined; roll: undefined }) {
    this.position = position

    if (orientation) {
      if (orientation.pitch !== undefined) {
        this.pitch = (orientation.pitch - 90) * -1
      }
      if (orientation.heading !== undefined) {
        this.heading = orientation.heading
      }
      if (orientation.roll !== undefined) {
        this.roll = orientation.roll
      }
    }

    this.add()
  }

  /**
     * 更新视场角
     * @param {number} fov - 新视场角
     */
  updateFov (fov: any) {
    this.fov = fov
    this.add()
  }

  /**
     * 显示/隐藏视锥体
     * @param {boolean} visible - 是否可见
     */
  setVisible (visible: any) {
    if (this.frustumPrimitive) {
      this.frustumPrimitive.show = visible
    }
    if (this.outlinePrimitive) {
      this.outlinePrimitive.show = visible
    }
    if (this.model) {
      this.model.show = visible
    }
  }

  /**
     * 清理所有可视化元素
     */
  clear () {
    this._removePrimitive(this.frustumPrimitive)
    this._removePrimitive(this.outlinePrimitive)
    this._removePrimitive(this.model)
    this.frustumPrimitive = null
    this.outlinePrimitive = null
    this.model = null
    this.frustumGeometry = null
  }

  /**
     * 添加GLB模型
     */
  async addGlbModel () {
    if (!this.position) return

    try {
      const orientation = new Cesium.HeadingPitchRoll(
        Cesium.Math.toRadians(this.heading),
        Cesium.Math.toRadians(this.pitch - 90),
        Cesium.Math.toRadians(this.roll)
      )

      const modelMatrix = Cesium.Transforms.headingPitchRollToFixedFrame(
        this.position,
        orientation
      )

      const model = await Cesium.Model.fromGltfAsync({
        url: this.modelUrl,
        modelMatrix: modelMatrix,
        scale: this.modelScale,
        minimumPixelSize: 0.1,
        maximumScale: 0.6,
        show: true
      })

      this.model = this.viewer.scene.primitives.add(model)
    } catch (error) {
      console.error('Failed to load GLB model:', error)
    }
  }

  // ==================== 私有方法 ====================

  /**
     * 更新方向四元数
     */
  _updateOrientation () {
    const headingPitchRoll = new Cesium.HeadingPitchRoll(
      Cesium.Math.toRadians(this.heading),
      Cesium.Math.toRadians(this.pitch),
      Cesium.Math.toRadians(this.roll)
    )

    this.orientation = Cesium.Transforms.headingPitchRollQuaternion(
      this.position,
      headingPitchRoll
    )
  }

  /**
     * 添加视锥体几何体
     */
  _addFrustum () {
    const frustum = this._createPerspectiveFrustum()

    this.frustumGeometry = new Cesium.FrustumGeometry({
      frustum: frustum,
      origin: this.position,
      orientation: this.orientation,
      vertexFormat: this.COLOR_FORMAT
    })

    const geometryInstance = new Cesium.GeometryInstance({
      geometry: this.frustumGeometry,
      attributes: {
        color: Cesium.ColorGeometryInstanceAttribute.fromColor(
          this._getFrustumColor()
        )
      }
    })

    const primitive = new Cesium.Primitive({
      geometryInstances: geometryInstance,
      appearance: new Cesium.PerInstanceColorAppearance({
        closed: true,
        flat: true
      }),
      asynchronous: false
    })

    this.frustumPrimitive = this.viewer.scene.primitives.add(primitive)
  }

  /**
     * 添加视锥体轮廓
     */
  _addOutline () {
    const frustum = this._createPerspectiveFrustum()

    const outlineGeometry = new Cesium.FrustumOutlineGeometry({
      frustum: frustum,
      origin: this.position,
      orientation: this.orientation
    })

    const geometryInstance = new Cesium.GeometryInstance({
      geometry: outlineGeometry,
      attributes: {
        color: Cesium.ColorGeometryInstanceAttribute.fromColor(
          this.DEFAULT_OUTLINE_COLOR
        )
      }
    })

    const primitive = new Cesium.Primitive({
      geometryInstances: geometryInstance,
      appearance: new Cesium.PerInstanceColorAppearance({
        closed: true,
        flat: true
      }),
      asynchronous: false
    })

    this.outlinePrimitive = this.viewer.scene.primitives.add(primitive)
  }

  /**
     * 更新模型位置
     */
  _updateModelPosition () {
    if (!this.model || !this.position) return

    const orientation = new Cesium.HeadingPitchRoll(
      Cesium.Math.toRadians(this.heading),
      Cesium.Math.toRadians(this.pitch - 90),
      Cesium.Math.toRadians(this.roll)
    )

    const modelMatrix = Cesium.Transforms.headingPitchRollToFixedFrame(
      this.position,
      orientation
    )

    this.model.modelMatrix = modelMatrix
  }

  /**
     * 创建透视视锥体
     */
  _createPerspectiveFrustum () {
    return new Cesium.PerspectiveFrustum({
      fov: Cesium.Math.toRadians(this.fov),
      aspectRatio: this.aspectRatio,
      near: this.near,
      far: this.far
    })
  }

  /**
     * 获取视锥体颜色
     */
  _getFrustumColor () {
    return new Cesium.Color(
      this.color[0] / 255,
      this.color[1] / 255,
      this.color[2] / 255,
      0.3 // 透明度
    )
  }

  /**
     * 安全移除图元
     */
  _removePrimitive (primitive: any) {
    if (primitive && this.viewer && this.viewer.scene) {
      this.viewer.scene.primitives.remove(primitive)
    }
  }

  // ==================== 工具方法 ====================

  /**
     * 根据航向获取偏移位置
     * @param {number} heading - 航向角
     * @param {number} distance - 偏移距离
     */
  getPositionByHeading (heading: number, distance = 2) {
    if (!this.position) return null

    const radHeading = Cesium.Math.toRadians(heading)
    const offsetX = -Math.cos(radHeading)
    const offsetY = Math.sin(radHeading)

    const enuMatrix = Cesium.Transforms.eastNorthUpToFixedFrame(this.position)
    const targetPoint = Cesium.Matrix4.multiplyByPoint(
      enuMatrix,
      new Cesium.Cartesian3(offsetX, offsetY, 0),
      new Cesium.Cartesian3()
    )

    const direction = Cesium.Cartesian3.normalize(
      Cesium.Cartesian3.subtract(this.position, targetPoint, new Cesium.Cartesian3()),
      new Cesium.Cartesian3()
    )

    return new Cesium.Cartesian3(
      this.position.x + direction.x * distance,
      this.position.y + direction.y * distance,
      this.position.z + direction.z * distance
    )
  }

  /**
     * 根据航向和俯仰获取位置
     * @param {number} heading - 航向角
     * @param {number} pitch - 俯仰角
     * @param {number} distance - 距离
     */
  getPositionByHeadingPitch (heading: number, pitch: number, distance: number) {
    if (!this.position) return null

    const radHeading = Cesium.Math.toRadians(heading)
    const radPitch = Cesium.Math.toRadians(pitch)

    const x = distance * Math.cos(radPitch) * Math.cos(radHeading)
    const y = distance * Math.cos(radPitch) * Math.sin(radHeading)
    const z = distance * Math.sin(radPitch)

    const enuMatrix = Cesium.Transforms.eastNorthUpToFixedFrame(this.position)
    return Cesium.Matrix4.multiplyByPoint(
      enuMatrix,
      new Cesium.Cartesian3(x, y, z),
      new Cesium.Cartesian3()
    )
  }

  /**
     * 销毁实例，释放资源
     */
  destroy () {
    this.clear()

    // 清除所有引用
    this.position = null
    this.orientation = null
    this.viewer = null
  }
}
